import { useGetIdentity, useApiUrl } from '@pankod/refine-core';
import { Avatar, Card, Modal, Upload, Button, message } from 'antd';
import React, { useCallback, useState, useEffect, MouseEvent } from 'react';
import { EditOutlined, SettingOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';
import { useNavigate } from '@pankod/refine-react-router-v6';
import axios from 'axios';
import UserHandler from 'util/UserHandler';

interface IconColors {
  setting: string;
  edit: string;
}

export const Profile: React.FC = () => {
  const { data: user, refetch } = useGetIdentity({
    queryOptions: {
      // Forcer l'invalidation du cache à chaque appel
      cacheTime: 0,
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
  });
  const navigate = useNavigate();
  const apiUrl = useApiUrl();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState<'avatar' | 'banner' | null>(null);
  const [avatarUrl, setAvatarUrl] = useState('https://joeschmoe.io/api/v1/random');
  const [bannerUrl, setBannerUrl] = useState(
    'https://gw.alipayobjects.com/zos/rmsportal/JiqGstEfoWAOHiTxclqi.png'
  );
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [iconColors, setIconColors] = useState<IconColors>({
    setting: '#000',
    edit: '#000',
  });

  // Construction des URLs des images
  const formatImageUrl = useCallback((filePath: string): string | undefined => {
    if (!filePath || typeof filePath !== 'string' || filePath.trim() === '') {
      return undefined;
    }
    const formattedPath = filePath.startsWith('http')
      ? filePath
      : encodeURI(`${process.env.REACT_APP_SERVER}${filePath}`);
    console.log('formatImageUrl - filePath:', filePath, 'formattedPath:', formattedPath);
    return formattedPath;
  }, []);

  // Fonction pour supprimer un fichier du serveur
  const deleteFileFromServer = async (filePath: string): Promise<boolean> => {
    try {
      console.log('Tentative de suppression du fichier:', filePath);
      const response = await fetch(`${apiUrl}/media/upload`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${UserHandler.getInstance().token}`,
          'Cache-Control': 'no-cache',
        },
        body: JSON.stringify({
          file: filePath,
        }),
      });
      
      if (response.ok) {
        console.log('Fichier supprimé avec succès:', filePath);
        return true;
      } else {
        console.error('Erreur lors de la suppression du fichier:', response.statusText);
        return false;
      }
    } catch (error) {
      console.error('Erreur lors de la suppression du fichier:', error);
      return false;
    }
  };

  // Charger les URLs initiales des images
  useEffect(() => {
    if (user) {
      console.log('Données utilisateur reçues:', {
        user_id: user.id,
        profil_url: user.profil_url,
        banner_url: user.banner_url,
      });

      const formattedAvatarUrl = user.profil_url
        ? formatImageUrl(user.profil_url)
        : 'https://joeschmoe.io/api/v1/random';
      const formattedBannerUrl = user.banner_url
        ? formatImageUrl(user.banner_url)
        : 'https://gw.alipayobjects.com/zos/rmsportal/JiqGstEfoWAOHiTxclqi.png';

      setAvatarUrl(`${formattedAvatarUrl}?t=${Date.now()}`);
      setBannerUrl(`${formattedBannerUrl}?t=${Date.now()}`);
    }
  }, [user, formatImageUrl]);

  const getUserName = useCallback((userData: any) => {
    const name = (userData?.firstname ?? 'John').charAt(0).toUpperCase() + (userData?.firstname ?? 'John').slice(1);
    const lastname = (userData?.lastname ?? 'Doe').charAt(0).toUpperCase() + (userData?.lastname ?? 'Doe').slice(1);
    return `${name} ${lastname}`;
  }, []);

  const customRequest = async ({ file, onSuccess, onError }: any) => {
    if (!user?.id) {
      message.error('Utilisateur non identifié');
      return;
    }
    console.log('customRequest - Téléchargement démarré pour:', file.name, 'userId:', user.id);
    const formData = new FormData();
    formData.append('file', file);
    const folder = uploadType === 'banner' ? 'banner' : 'profile';
    formData.append('table', `users/${user.id}/${folder}`);

    try {
      const response = await axios.post(`${apiUrl}/media/upload`, formData, {
        headers: {
          Authorization: `Bearer ${UserHandler.getInstance().token}`,
          'Content-Type': 'multipart/form-data',
          'Cache-Control': 'no-cache',
        },
      });
      console.log('customRequest - Succès:', response.data);
      onSuccess(response.data, file);
    } catch (error) {
      console.error('customRequest - Erreur:', error);
      onError(error);
      message.error(`Échec du téléchargement de ${file.name}`);
    }
  };

  const handleUploadChange: UploadProps['onChange'] = async ({ file, fileList: newFileList }) => {
    if (!user?.id) {
      message.error('Utilisateur non identifié');
      return;
    }
    console.log('handleUploadChange - Nouvelle liste de fichiers:', newFileList, 'userId:', user.id);
    const updatedFileList = newFileList.map((f) => ({
      ...f,
      url: f.response ? formatImageUrl(f.response.filePath) ?? f.url : f.url,
    }));
    setFileList(updatedFileList);

    if (file.status === 'done') {
      const filePath = file.response?.filePath;
      if (filePath && uploadType) {
        const fullUrl = formatImageUrl(filePath);
        console.log('URL générée pour l\'image:', fullUrl);

        try {
          if (uploadType === 'avatar' && fullUrl) {
            // Supprimer l'ancien fichier avatar s'il existe
            if (user.profil_url && !user.profil_url.startsWith('http')) {
              await deleteFileFromServer(user.profil_url);
            }

            setAvatarUrl(`${fullUrl}?t=${Date.now()}`);
            const patchResponse = await axios.patch(
              `${apiUrl}/user/${user.id}`,
              { profil_url: filePath },
              {
                headers: {
                  Authorization: `Bearer ${UserHandler.getInstance().token}`,
                  'Content-Type': 'application/json',
                  'Cache-Control': 'no-cache',
                },
              }
            );
            console.log('PATCH profil_url - Réponse:', patchResponse.data);
            
            // Vérifier les données utilisateur après la mise à jour
            const verifyResponse = await axios.get(`${apiUrl}/user/${user.id}`, {
              headers: {
                Authorization: `Bearer ${UserHandler.getInstance().token}`,
                'Cache-Control': 'no-cache',
              },
            });
            console.log('Vérification des données utilisateur:', verifyResponse.data);
            message.success('Photo de profil mise à jour avec succès !');
            await refetch();
          } else if (uploadType === 'banner' && fullUrl) {
            // Supprimer l'ancien fichier banner s'il existe
            console.log('Banner - user.banner_url:', user.banner_url);
            console.log('Banner - Est-ce une URL externe?', user.banner_url?.startsWith('http'));
            if (user.banner_url && !user.banner_url.startsWith('http')) {
              console.log('Suppression de l\'ancien banner:', user.banner_url);
              await deleteFileFromServer(user.banner_url);
            } else {
              console.log('Pas de suppression nécessaire pour le banner');
            }

            setBannerUrl(`${fullUrl}?t=${Date.now()}`);
            const patchResponse = await axios.patch(
              `${apiUrl}/user/${user.id}`,
              { banner_url: filePath },
              {
                headers: {
                  Authorization: `Bearer ${UserHandler.getInstance().token}`,
                  'Content-Type': 'application/json',
                  'Cache-Control': 'no-cache',
                },
              }
            );
            console.log('PATCH banner_url - Réponse:', patchResponse.data);
            
            // Vérifier les données utilisateur après la mise à jour
            const verifyResponse = await axios.get(`${apiUrl}/user/${user.id}`, {
              headers: {
                Authorization: `Bearer ${UserHandler.getInstance().token}`,
                'Cache-Control': 'no-cache',
              },
            });
            console.log('Vérification des données utilisateur:', verifyResponse.data);
            message.success('Bannière mise à jour avec succès !');
            await refetch();
          }
          setIsModalOpen(false);
        } catch (error) {
          console.error(`Erreur lors de la mise à jour de ${uploadType === 'avatar' ? 'profil_url' : 'banner_url'}:`, error);
          message.error(`Échec de la mise à jour de ${uploadType === 'avatar' ? 'la photo de profil' : 'la bannière'}.`);
        }
      }
    } else if (file.status === 'error') {
      message.error("L'upload a échoué. Veuillez réessayer.");
    }
  };

  const handleRemove = async (file: UploadFile) => {
    if (!user?.id) {
      message.error('Utilisateur non identifié');
      return;
    }
    console.log('handleRemove - Tentative de suppression du fichier:', file, 'userId:', user.id);
    if (file.response?.filePath) {
      const deleteSuccess = await deleteFileFromServer(file.response.filePath);
      
      if (deleteSuccess) {
        if (uploadType === 'avatar') {
          setAvatarUrl(`https://joeschmoe.io/api/v1/random?t=${Date.now()}`);
          const patchResponse = await axios.patch(
            `${apiUrl}/user/${user.id}`,
            { profil_url: null },
            {
              headers: {
                Authorization: `Bearer ${UserHandler.getInstance().token}`,
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
              },
            }
          );
          console.log('PATCH profil_url (suppression) - Réponse:', patchResponse.data);
        } else if (uploadType === 'banner') {
          setBannerUrl(`https://gw.alipayobjects.com/zos/rmsportal/JiqGstEfoWAOHiTxclqi.png?t=${Date.now()}`);
          const patchResponse = await axios.patch(
            `${apiUrl}/user/${user.id}`,
            { banner_url: null },
            {
              headers: {
                Authorization: `Bearer ${UserHandler.getInstance().token}`,
                'Content-Type': 'application/json',
                'Cache-Control': 'no-cache',
              },
            }
          );
          console.log('PATCH banner_url (suppression) - Réponse:', patchResponse.data);
        }
        
        await refetch();
        message.success('Image supprimée avec succès');
      } else {
        message.error('Échec de la suppression du fichier');
      }
    }
  };

  const onPreview = async (file: UploadFile) => {
    console.log('onPreview - Fichier à prévisualiser:', file);
    let src = file.url as string;
    if (!src && file.response?.filePath) {
      const formattedUrl = formatImageUrl(file.response.filePath);
      src = formattedUrl || '';
    } else if (!src && file.originFileObj) {
      src = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.originFileObj as Blob);
        reader.onload = () => resolve(reader.result as string);
      });
    }
    const image = new Image();
    image.src = src;
    const imgWindow = window.open(src);
    imgWindow?.document.write(image.outerHTML);
  };

  const openUploadModal = (type: 'avatar' | 'banner') => {
    if (!user?.id) {
      message.error('Utilisateur non identifié');
      return;
    }
    setUploadType(type);
    setFileList([]);
    setIsModalOpen(true);
    console.log('Ouverture du modal pour:', type, 'userId:', user.id);
  };

  const handleAvatarMouseEnter = (e: MouseEvent<HTMLElement>) => {
    e.currentTarget.style.transform = 'translateX(-50%) scale(1.05)';
  };

  const handleAvatarMouseLeave = (e: MouseEvent<HTMLElement>) => {
    e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
  };

  const handleButtonMouseEnter = (e: MouseEvent<HTMLElement>) => {
    e.currentTarget.style.transform = 'scale(1.1)';
  };

  const handleButtonMouseLeave = (e: MouseEvent<HTMLElement>) => {
    e.currentTarget.style.transform = 'scale(1)';
  };

  const handleIconHover = (key: 'setting' | 'edit', enter: boolean) => {
    setIconColors((prev: IconColors) => ({
      ...prev,
      [key]: enter ? '#1890ff' : '#000',
    }));
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>, type: 'avatar' | 'banner') => {
    console.error(`Erreur de chargement de l'image ${type}:`, e.currentTarget.src);
    if (type === 'avatar') {
      setAvatarUrl(`https://joeschmoe.io/api/v1/random?t=${Date.now()}`);
    } else {
      setBannerUrl(`https://gw.alipayobjects.com/zos/rmsportal/JiqGstEfoWAOHiTxclqi.png?t=${Date.now()}`);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '20vh',
        padding: '32px',
      }}
    >
      <Card
        style={{
          width: 400,
          borderRadius: 16,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
          overflow: 'hidden',
          background: '#fff',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        }}
        hoverable
        cover={
          <div style={{ position: 'relative', textAlign: 'center' }}>
            <img
              alt="banner"
              src={`${bannerUrl}?t=${Date.now()}`}
              onError={(e) => handleImageError(e, 'banner')}
              style={{
                width: '100%',
                height: 160,
                objectFit: 'cover',
                borderBottom: '4px solid #1890ff',
              }}
            />
            <div
              onClick={() => openUploadModal('avatar')}
              onMouseEnter={handleAvatarMouseEnter}
              onMouseLeave={handleAvatarMouseLeave}
              style={{
                position: 'absolute',
                bottom: -65,
                left: '50%',
                transform: 'translateX(-50%)',
                border: '6px solid #ffffff',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                borderRadius: '50%',
              }}
            >
              <Avatar
                size={130}
                src={`${avatarUrl}?t=${Date.now()}`}
                style={{
                  display: 'block',
                }}
              />
            </div>
            <Button
              icon={<UploadOutlined />}
              style={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                background: '#1890ff',
                color: '#fff',
                borderRadius: '50%',
                padding: 8,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
                border: 'none',
                transition: 'transform 0.2s',
              }}
              onClick={() => openUploadModal('banner')}
              onMouseEnter={handleButtonMouseEnter}
              onMouseLeave={handleButtonMouseLeave}
            />
          </div>
        }
        actions={[
          <SettingOutlined
            key="setting"
            style={{ fontSize: 22, color: iconColors.setting, transition: 'color 0.3s' }}
            onMouseEnter={() => handleIconHover('setting', true)}
            onMouseLeave={() => handleIconHover('setting', false)}
          />,
          <EditOutlined
            key="edit"
            style={{ fontSize: 22, color: iconColors.edit, transition: 'color 0.3s' }}
            onMouseEnter={() => handleIconHover('edit', true)}
            onMouseLeave={() => handleIconHover('edit', false)}
            onClick={() => {
              if (user?.id) {
                navigate(`/user/edit/${user.id}`);
              } else {
                message.error('Aucun utilisateur sélectionné');
              }
            }}
          />,
        ]}
      >
       <div style={{ marginTop: 80, textAlign: 'center' }}>
 <div style={{
  textAlign: 'center',
  padding: '16px 0'
}}>
  <h2 style={{
    margin: 0,
    fontWeight: 700,
    fontSize: '24px',
    color: '#333',
    textAlign: 'center'
  }}>
    {user ? getUserName(user) : 'Invité'}
  </h2>
  <p style={{
    color: '#777',
    fontSize: '15px',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: '8px'
  }}>
    {user?.email ?? 'Email non disponible'}
  </p>
</div>
</div>
      </Card>

      <Modal
        title={`Upload ${uploadType === 'avatar' ? 'Photo de profil' : 'Bannière'}`}
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <Upload
          accept="image/*"
          showUploadList={true}
          customRequest={customRequest}
          onChange={handleUploadChange}
          onRemove={handleRemove}
          fileList={fileList}
          onPreview={onPreview}
          listType="picture"
        >
          {fileList.length === 0 && (
            <Button icon={<UploadOutlined />}>Choisir une image</Button>
          )}
        </Upload>
      </Modal>
    </div>
  );
};

export default Profile;